import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router';
import { ScrollArea } from '../components/ui/scroll-area';
import { StockHeroStrip, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';

export default function Terms() {
  const navigate = useNavigate();

  return (
    <WarmMarketingBackdrop>
      <div className="mx-auto max-w-4xl px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          aria-label="Go back"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <StockHeroStrip stockKey="handsWalking" alt="Illustrative stock imagery" />
        <div className="rounded-xl border border-violet-100/90 bg-white/95 p-8 shadow-lg">
          <h1 className="text-3xl font-light mb-2">Terms of Service</h1>
          <p className="text-sm text-neutral-500 mb-8">Last updated: March 5, 2026</p>

          <ScrollArea className="h-[calc(100vh-240px)]">
            <div className="space-y-6 pr-4">
              <section>
                <h2 className="text-xl font-medium mb-3">1. Acceptance of Terms</h2>
                <p className="text-neutral-700">
                  By accessing or using TENSION ("the App"), you agree to these Terms of Service.
                  If you do not agree, do not use the App.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">2. Age Requirement</h2>
                <p className="text-neutral-700">
                  You must be at least 18 years old to use TENSION. By using the App, you confirm that you
                  are 18 or older.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">3. Not Medical or Therapeutic Advice</h2>
                <p className="text-neutral-700 mb-3">
                  <strong className="text-red-600">IMPORTANT DISCLAIMER:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                  <li>TENSION is NOT a substitute for professional mental health care, therapy, or medical advice.</li>
                  <li>The App does not provide diagnosis, treatment, or clinical counseling.</li>
                  <li>The tension score (RTI) is a self-assessment tool, not a clinical measure.</li>
                  <li>Action cards and recommendations are educational suggestions, not therapeutic interventions.</li>
                  <li>If you are experiencing a mental health crisis, contact emergency services or a crisis hotline immediately.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">4. User Responsibilities</h2>
                <p className="text-neutral-700 mb-3">You agree to:</p>
                <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                  <li>Provide accurate information in your check-ins and assessments</li>
                  <li>Use the App for personal, non-commercial purposes only</li>
                  <li>Keep your account credentials secure</li>
                  <li>Not share or sell your account access</li>
                  <li>Not attempt to reverse engineer, hack, or exploit the App</li>
                  <li>Seek professional help when needed</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">5. Privacy and Data</h2>
                <p className="text-neutral-700">
                  Your use of TENSION is also governed by our Privacy Policy. We describe what we collect, where it is
                  stored (including local-first relationship data vs. account data on Supabase), and how we protect it
                  (TLS, vendor security, no sale of personal data). Please review our{' '}
                  <button
                    onClick={() => navigate('/privacy')}
                    className="text-purple-600 hover:text-purple-700 underline"
                  >
                    Privacy Policy
                  </button>{' '}
                  for details.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">6. Subscription and Payments</h2>
                <p className="text-neutral-700 mb-3">
                  TENSION offers subscription plans (Premium and Pro) and one-time purchases (Situational Packs):
                </p>
                <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                  <li>Subscriptions auto-renew unless canceled before the renewal date</li>
                  <li>You can cancel your subscription at any time in account settings</li>
                  <li>Refunds follow our refund policy and any applicable app store policies</li>
                  <li>Prices may change with 30 days notice for existing subscribers</li>
                  <li>One-time purchases (Situational Packs) are non-refundable unless required by law</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">7. Intellectual Property Rights</h2>
                <p className="text-neutral-700 mb-3">
                  <strong>All rights reserved.</strong> The TENSION application and all related intellectual property
                  are owned exclusively by [YOUR FULL LEGAL NAME].
                </p>
                <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                  <li><strong>Software Ownership:</strong> All source code, algorithms, and technical implementations are proprietary</li>
                  <li><strong>RTI Methodology:</strong> The tension score (RTI) framework is a proprietary methodology</li>
                  <li><strong>Content:</strong> All action cards, prompts, educational content, and micro-actions are copyrighted</li>
                  <li><strong>Branding:</strong> "TENSION", "RTI", logos, and related trademarks are protected marks</li>
                  <li><strong>Your Data:</strong> You retain ownership of your personal data (check-ins, journal entries, etc.)</li>
                  <li><strong>License Grant:</strong> You receive a limited, non-exclusive, non-transferable license to use the App</li>
                  <li><strong>Restrictions:</strong> You may not copy, modify, distribute, sell, or reverse engineer any part of the App</li>
                  <li><strong>Feedback:</strong> Any suggestions or feedback you provide may be used without compensation</li>
                </ul>
                <p className="text-neutral-700 mt-3">
                  For licensing inquiries or IP questions, contact: [YOUR EMAIL ADDRESS]
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">8. Account Termination</h2>
                <p className="text-neutral-700">
                  We may suspend or terminate your account if you violate these Terms.
                  You may delete your account at any time through the Settings page. Account deletion is permanent
                  and cannot be undone.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">9. Limitation of Liability</h2>
                <p className="text-neutral-700">
                  TENSION and its creators are not liable for damages arising from your use of the App,
                  including relationship outcomes, emotional distress, or decisions made
                  based on App recommendations. The App is provided "as is" without warranties of any kind.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">10. Changes to Terms</h2>
                <p className="text-neutral-700">
                  We may update these Terms from time to time. Continued use of the App after changes are posted
                  means you accept the updated Terms. We will notify users of material changes via email
                  or in-app notification.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">11. Governing Law</h2>
                <p className="text-neutral-700">
                  These Terms are governed by the laws of the jurisdiction where the App operator is located.
                  Any disputes will be resolved in the courts of that jurisdiction.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">12. Contact</h2>
                <p className="text-neutral-700">
                  For questions about these Terms, please contact us through the in-app feedback system or
                  at support@tensionapp.com.
                </p>
              </section>

              <div className="tension-glow mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Remember:</strong> TENSION is a tool for self-awareness and relationship health tracking.
                  It is not a replacement for professional help. If you're in crisis, please contact emergency
                  services or a crisis hotline immediately.
                </p>
              </div>
            </div>
          </ScrollArea>
        </div>
      </div>
    </WarmMarketingBackdrop>
  );
}
